import React, { useContext } from 'react';
import { action, observable, Reaction, makeObservable, makeAutoObservable } from 'mobx';

export const MobxStoreContext = React.createContext({});

export class ObservableSet<T> {
    map: Map<T, boolean>;

    constructor(values?: T[]) {
        makeObservable(this, {
            set: action.bound,
            clear: action.bound
        });

        this.map = values
            ? observable.map(values.map((v: any): [T, boolean] => [v, true]))
            : observable.map();
    }

    add(value: T) {
        this.map.set(value, true);
        return this;
    }

    delete(value: T) {
        return this.map.delete(value);
    }

    has(value: T) {
        return this.map.has(value);
    }

    set(values: T[]) {
        this.clear();
        for (let v of values) {
            this.add(v);
        }
    }

    forEach(callback: (value: boolean, key: T, map: Map<T, boolean>) => void) {
        return this.map.forEach(callback);
    }

    clear() {
        this.map.clear();
    }

    get size() {
        return this.map.size;
    }

    [Symbol.iterator]() {
        return this.map.keys();
    }
}

export class TrackingStateStore {
    tracking = true;
  
    constructor() {
      makeAutoObservable(this);
    }
  
    setTracking(value: boolean) {
      this.tracking = value;
    }
  }

  export const trackingState = new TrackingStateStore();

/** recovers a parent level 'Mobx Provided' react context */
export function useStore<T>(store: string): T {
    const stores = useContext(MobxStoreContext) as { [key: string]: T };
    return stores[store] as T;
}

/** wraps children in a mobx provider fed by a react state object  */
export function Provider({ children, ...stores }: { children: JSX.Element; [key: string]: any }) {
    return <MobxStoreContext.Provider value={stores}>{children}</MobxStoreContext.Provider>;
}
